import _ from 'lodash';
import 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import useGlobalConfigs from '@/stores/globalConfigs';

const emrTypeHelper = {
  canEdit(emr: IEMRData, race: (string | undefined)[] = ['phr', undefined]) {
    return race.includes(emr?.race);
  },

  getFieldConfig(emrType: IEMRType, field: string) {
    return emrType?.format?.fields?.find((f) => f.key === field);
  },

  getFieldByKey(key: string = '', emrType?: IEMRType) {
    const ks = key.split('.') || [];
    if (!emrType) {
      const emrKey = ks.shift();
      emrType = useGlobalConfigs.getState().getEmrType(emrKey as string);
    }
    const getConfig = (e?: IEMRType): IFieldConfig | undefined => {
      const k = ks.shift();
      const f = e?.format?.fields?.find((f) => f.key === k);
      if (ks?.length) {
        return getConfig(f?.emr);
      } else {
        return f;
      }
    };
    return getConfig(emrType);
  },

  getSubEmrConfig(emrType: IEMRType, field: string) {
    return emrType?.format?.fields?.find((f) => f.key === field)?.emr;
  },

  getOption(fieldConfig: IFieldConfig, val: string) {
    return fieldConfig?.extra?.options?.find((o) => o.value === val);
  },

  generateContent(emr: IEMRData, config: IEMRType, format?: string) {
    const extractBracketsContent = (str: string) => {
      const pattern = /\{(.*?)\}/g;
      const matches: string[] = [];
      let match;

      while ((match = pattern.exec(str)) !== null) {
        matches.push(match[1]);
      }

      return matches;
    };
    const getData = (value: any, config: IEMRType, data: any = {}) => {
      const coreValue = value?.data || value;
      const timelineConfig: ITimelineConfig = config?.extra?.timeline || {};
      const stringFormat = format || timelineConfig.format;
      if (!stringFormat) return '';

      extractBracketsContent(stringFormat).forEach((k: string) => {
        const val = _.get(coreValue, k);

        const fieldConfig = emrTypeHelper.getFieldConfig(config, k);
        const subEmr = fieldConfig?.emr;

        if (subEmr) {
          data[k] = val?.map((v: any) => getData(v, subEmr)).join('\n');
        } else if (fieldConfig?.extra?.options) {
          const raw = emrTypeHelper.getOption(fieldConfig, val);
          data[k] = raw ? `<span style='color:${raw?.color}; font-weight:bold'>${raw?.label}</span>` : undefined;
        } else if (fieldConfig?.inputType === 'number' && val) {
          data[k] = emrTypeHelper.toFix(fieldConfig, val);
        } else {
          data[k] = val;
        }
      });
      return stringFormat.replace(/\{([\w.]+)\}/g, (match: any, key: string | number) => {
        return typeof data?.[key] !== 'undefined' ? data?.[key] : '';
      });
    };

    const data = getData(emr, config);
    return data;
  },

  transformData(obj: any = []) {
    const o = _.cloneDeep(obj);

    const handler = (objInput: any = []) => {
      Object.keys(objInput).forEach((k: string) => {
        const v = objInput[k];
        if (_.isArray(v)) {
          objInput[k] = v.map((d: any) => {
            if (d.data) handler(d.data);
            return d.data || d;
          });
        }
      });
    };

    handler(o);

    return o;
  },

  drawWave(container: any, classSVG: string, waveWidth: number) {
    const containerRect = container.getBoundingClientRect();

    const points = Array.from(container.querySelectorAll('.point')).filter(Boolean);
    const anchors: { x: number; y: number }[] = points.map((p: any) => {
      const { x, y } = p.getBoundingClientRect();
      return { x: x + 2 - containerRect.x, y: y - containerRect.y };
    });

    const waveHeight = anchors[0].y;

    anchors.push({
      x: waveWidth / 2,
      y: anchors[anchors.length - 1].y + waveHeight * 2,
    });

    anchors.unshift({
      x: waveWidth / 2,
      y: 0,
    });

    const rawPath = MotionPathPlugin.arrayToRawPath(anchors, {
      curviness: 1.5,
    });
    const svg = this.buildPath(anchors, rawPath);

    container.querySelector('.svg-timeline')?.remove();
    svg.setAttribute('class', classSVG + ' svg-timeline');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', '1');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    container.appendChild(svg);
  },

  buildPath(anchors: any, rawPath: any) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    function createSVG(type: any, container: any, attributes: any) {
      const element = document.createElementNS('http://www.w3.org/2000/svg', type);

      for (const p in attributes) {
        element.setAttributeNS(null, p.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase(), attributes[p]);
      }
      element.setAttribute('color', 'red');
      element.setAttribute('class', 'curve');

      container.appendChild(element);
      return element;
    }

    createSVG('path', svg, {
      d: MotionPathPlugin.rawPathToString(rawPath),
    });

    return svg;
  },

  toFix(fieldConfig?: IFieldConfig, value?: any) {
    const toFixed = fieldConfig?.extra?.parser?.toFixed;
    if ((!_.isNumber(value) && typeof toFixed !== 'number') || toFixed === undefined) return value;

    if ((String(value)?.split('.')?.[1]?.length as number) > toFixed) {
      return Number(Number(value).toFixed(toFixed));
    }
    return _.isNumber(value) ? Number(value) : value ?? null;
  },
  toFixByKeyConfig(value: any, emrType: IEMRType, key: string) {
    const fieldConfig = emrTypeHelper.getFieldByKey(key, emrType);

    const toFixed = fieldConfig?.extra?.parser?.toFixed;
    if ((!_.isNumber(value) && typeof toFixed !== 'number') || toFixed === undefined) return value ?? null;

    if ((String(value)?.split('.')?.[1]?.length as number) > toFixed) {
      return Number(Number(value).toFixed(toFixed));
    }

    return _.isNumber(value) ? Number(value) : value ?? null;
  },
};
export default emrTypeHelper;
